import { ArrowBack, Close, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
// Components
import { Button as SMButton, Loader, OrderManagementPage, PageTitle } from '../../../components';
import { UserContext } from '../../../context';
import { getAdminRole, getAdminRoleCategories, updateAdminRole } from '../../../fetch';
import { formatMoney, isAllowedToBeEditedOrDeleted } from '../../../helpers';
import { IRoleCategory, IRoleDetail, RoleType } from '../../../models';
import { CategoryListItem } from './CategoryListItem';
import { DownloadCSV } from './DownloadCSV';

interface IDetailRole {}

export const DetailRole: FC<IDetailRole> = () => {
  const history = useHistory();
  const { id }: { id: string } = useParams();
  const { isGlobalAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const [modifiedRole, setModifiedRole] = useState<IRoleDetail | null>(null);
  const [isLoadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [modifiedName, setModifiedName] = useState('');
  const [modifiedSpendLimitChangeOrder, setModifiedSpendLimitChangeOrder] = useState('');
  const [modifiedSpendLimit, setModifiedSpendLimit] = useState('');
  const [actualSpendLimitChangeOrder, setActualSpendLimitChangeOrder] = useState<number>(0);
  const [actualSpendLimit, setActualSpendLimit] = useState<number>(0);
  const [modifiedRoleType, setModifiedRoleType] = useState<RoleType | ''>('');
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [roleCategories, setRoleCategories] = useState<IRoleCategory[] | null>(null);
  const [partIds, setPartIds] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState(null);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      if (id !== 'add') {
        const roleDetail = await getAdminRole(Number(id));

        setModifiedRole(roleDetail);
        setModifiedName(roleDetail.name);
        setModifiedSpendLimitChangeOrder(formatMoney(roleDetail.spendLimitChangeOrder, 0));
        setModifiedSpendLimit(formatMoney(roleDetail.spendLimit, 0));
        setActualSpendLimitChangeOrder(roleDetail.spendLimitChangeOrder);
        setActualSpendLimit(roleDetail.spendLimit);
        setModifiedRoleType(roleDetail.roleType);
      }
    } catch (error) {
      enqueueSnackbar('Error loading role, please refresh and try again.', { variant: 'error' });
      console.error(error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchRoleCategories = async () => {
    try {
      if (isLoadingCategories) return;

      setLoadingCategories(true);

      var roleId = modifiedRole ? modifiedRole.placeOrderRoleId : id !== 'add' ? Number(id) : null;
      const res = await getAdminRoleCategories(roleId);

      setRoleCategories(res.hierarchy);
      setRoleAssignments(res.roleAssignments);
      setPartIds(res.roleAssignments?.flatMap(x => x.approvedPartIds) ?? []);
    } catch (error) {
      enqueueSnackbar('Error loading categories, please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchRoleCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedRole]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title =
    id === 'add'
      ? `Add a New Role`
      : modifiedRole
      ? `Edit Role${modifiedRole.name ? ` - ${modifiedRole.name}` : ''}`
      : 'Edit Role';
  return (
    <>
      <OrderManagementPage title={title}>
        <Grid container className={classes.titleWrapper}>
          <Grid item>
            <IconButton
              title={`Back to Manage Roles`}
              onClick={() => history.push('/production/orders/manage-roles')}
              size="large"
            >
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitle showDivider={false} title={title} />
          </Grid>
        </Grid>
        <Divider />
        <div className={classes.content}>
          {isLoadingRoles && isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
            <Box marginTop="16px">
              <Loader position="centered" type="inline" />
            </Box>
          )}
          {!isLoadingRoles && (
            <>
              <Grid container className={classes.roleContainer} spacing={1}>
                <Grid
                  className={classes.roleItem}
                  item
                  xs={12}
                  sm={isAllowedToBeEditedOrDeleted(modifiedRole?.name) ? 3 : 12}
                >
                  <TextField
                    fullWidth
                    required
                    disabled={!isGlobalAdmin}
                    variant="outlined"
                    size="small"
                    name="name"
                    autoComplete="none"
                    label="Name"
                    value={modifiedName}
                    onChange={event => {
                      setModifiedName(event.target.value);
                    }}
                  />
                </Grid>

                {isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
                  <Grid className={classes.roleItem} item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="spendLimit"
                      autoComplete="none"
                      label="Materials Spend Limit"
                      value={modifiedSpendLimit}
                      onBlur={e => {
                        const re = /^[0-9\b]+$/;
                        // Need to strip out all non numeric values from formatted value
                        const value = e.target.value
                          .replace('$', '')
                          .replace(',', '')
                          .replace('-', '');
                        // make sure it is number only before we update state
                        if (value === '' || re.test(value)) {
                          setActualSpendLimit(Number(value));
                        }
                        setModifiedSpendLimit(formatMoney(e.target.value.replace('-', ''), 0));
                      }}
                      onChange={e => {
                        setModifiedSpendLimit(e.target.value);
                      }}
                    />
                  </Grid>
                )}
                {isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
                  <Grid className={classes.roleItem} item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="spendLimit"
                      autoComplete="none"
                      label="Change Order Spend Limit"
                      value={modifiedSpendLimitChangeOrder}
                      onBlur={e => {
                        const re = /^[0-9\b]+$/;
                        // Need to strip out all non numeric values from formatted value
                        const value = e.target.value
                          .replace('$', '')
                          .replace(',', '')
                          .replace('-', '');
                        // make sure it is number only before we update state
                        if (value === '' || re.test(value)) {
                          setActualSpendLimitChangeOrder(Number(value));
                        }
                        setModifiedSpendLimitChangeOrder(
                          formatMoney(e.target.value.replace('-', ''), 0)
                        );
                      }}
                      onChange={e => {
                        setModifiedSpendLimitChangeOrder(e.target.value);
                      }}
                    />
                  </Grid>
                )}
                {isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
                  <Grid className={classes.roleItem} item xs={12} sm={3}>
                    <FormControl fullWidth variant="outlined" size="small" required>
                      <InputLabel id="role-type-label">Role Type</InputLabel>
                      <Select
                        labelId="role-type-label"
                        value={modifiedRoleType}
                        onChange={e => setModifiedRoleType(e.target.value as RoleType | '')}
                        label="Role Type"
                        disabled={id !== 'add' && !modifiedRole?.canEditRoleType}
                      >
                        <MenuItem value="Foreman">Foreman</MenuItem>
                        <MenuItem value="Warranty">Warranty</MenuItem>
                        <MenuItem value="Superintendent">Superintendent</MenuItem>
                        <MenuItem value="ProjectManager">Project Manager</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                {!isLoadingCategories &&
                  roleAssignments &&
                  isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
                    <Grid className={classes.roleItem} item xs={12}>
                      <Stack
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 0 }}
                      >
                        <Typography variant="h6">Select Assignments</Typography>
                        <DownloadCSV roleName={modifiedRole.name} roleCategories={roleCategories} />
                      </Stack>
                      <List
                        component="div"
                        aria-labelledby="nested-list-subheader"
                        className={classes.list}
                      >
                        {partIds &&
                          roleCategories &&
                          roleCategories.length > 0 &&
                          roleCategories.map((cat, index) => {
                            return (
                              <Fragment key={`CategoryListItem${index}`}>
                                <CategoryListItem
                                  category={cat}
                                  index={index}
                                  roleCategories={roleCategories}
                                  setRoleCategories={setRoleCategories}
                                  setPartIds={setPartIds}
                                  partIds={partIds}
                                  roleAssignments={roleAssignments}
                                  setRoleAssignments={setRoleAssignments}
                                  roles={[
                                    { id: modifiedRole.placeOrderRoleId, name: modifiedRole.name }
                                  ]}
                                />
                              </Fragment>
                            );
                          })}
                      </List>
                    </Grid>
                  )}
                {isLoadingCategories && isAllowedToBeEditedOrDeleted(modifiedRole?.name) && (
                  <Box marginTop="16px" display="flex" justifyContent="center" width="100%">
                    <Loader position="centered" type="inline" />
                  </Box>
                )}
              </Grid>

              <Grid container alignItems="center" className={classes.saveWrapper}>
                <ButtonGroup>
                  <SMButton
                    id="save"
                    color="primary"
                    variant="contained"
                    disabled={
                      !modifiedName ||
                      !modifiedSpendLimitChangeOrder ||
                      !modifiedSpendLimit ||
                      !modifiedRoleType
                    }
                    loading={isSaving ? 'Saving...' : ''}
                    startIcon={<Save />}
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await updateAdminRole({
                          id: modifiedRole ? modifiedRole.placeOrderRoleId : undefined,
                          name: modifiedName,
                          spendLimitChangeOrder: actualSpendLimitChangeOrder,
                          spendLimit: actualSpendLimit,
                          partIds: partIds,
                          roleType: modifiedRoleType as RoleType,
                          // removed for now and just send an empty array
                          reportsToRoleIds: []
                          // reportsToRoleIds: !selectedGroupName ? [] : [groups.filter(group => group.name === selectedGroupName)[0].id]
                        });
                        enqueueSnackbar('Role Updated!', { variant: 'success' });
                      } catch (error) {
                        const errorMessage = error?.response?.data?.Detail || 'Error updating role';
                        enqueueSnackbar(errorMessage, { variant: 'error' });
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Save
                  </SMButton>
                  <Button
                    id="remove"
                    variant="contained"
                    color="inherit"
                    startIcon={<Close />}
                    onClick={() => {
                      history.push('/production/orders/manage-roles');
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Grid>
            </>
          )}
        </div>
      </OrderManagementPage>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(0.5)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  roleContainer: {
    marginTop: 8,
    marginBottom: theme.spacing(1)
  },
  roleItem: {},
  roleItemSelectCategory: {
    paddingTop: `0 !important`
  },
  addCategoryButton: {},
  inputLabelCategory: {
    top: 0,
    left: 0,
    position: 'absolute'
  },
  addButton: {
    flex: 1
  },
  validationMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  list: {
    maxHeight: `30rem`,
    overflow: 'auto',
    margin: theme.spacing(1, 0)
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  }
}));
